import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../hooks/useApi';
import useRefreshOnFocus from '../../hooks/useRefreshOnFocus';
import { useApp } from '../../context';
import { formatDate, formatDateTime } from '../../utils/format';
import {
  RENEWAL_TABS, KIND_FILTERS, STATES, daysUntil, relativeDays, startedBy, recordStillOld,
  inFlight, completed, unraised, summary, calendarEntries, expiryRef, monthWeeks, addDays,
} from '../../utils/clearanceRenewals';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { CLEARANCE_RENEWALS_TOUR_ID, clearanceRenewalsTourSteps } from './clearanceRenewalsTour';

// Clearance & RDN Renewals: where every security clearance, site pass and RDN
// renewal has got to, for people outside the Project Management Office. The
// PMO works these as Actions in My Actions; this page reads them, along with
// every current expiry date, and changes nothing. Click any row or calendar
// chip to open its panel, with the Action's thread.

// actions and action_messages stamp 'YYYY-MM-DD HH:MM:SS' in UTC with no zone marker.
const stamp = (ts) => (ts ? ts.replace(' ', 'T') + (ts.endsWith('Z') ? '' : 'Z') : null);
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function StateTag({ state }) {
  const s = STATES[state];
  return <span className={`inline-flex px-1.5 py-px rounded-md border text-[9px] font-semibold whitespace-nowrap ${s.tone}`}>{s.label}</span>;
}

function KindTag({ label }) {
  return <span className="inline-flex px-1.5 py-px rounded-md text-[9px] font-bold bg-navy text-white whitespace-nowrap">{label}</span>;
}

function Due({ date, today, open }) {
  if (!date) return <span className="text-gray-400">-</span>;
  const n = daysUntil(date, today);
  const tone = !open ? 'text-gray-500' : n < 0 ? 'text-red-600' : n <= 7 ? 'text-amber-700' : 'text-gray-500';
  return (
    <span className="whitespace-nowrap">
      <span className="text-[10px] text-navy">{formatDate(date)}</span>
      {open && <span className={`block text-[8px] ${tone}`}>{n < 0 ? `expired ${relativeDays(date, today)}` : `expires ${relativeDays(date, today)}`}</span>}
    </span>
  );
}

function Tile({ value, label, tone, active, onClick, tour }) {
  return (
    <button data-tour={tour} onClick={onClick}
      className={`px-2 py-1 rounded border text-[10px] font-semibold flex items-center gap-1.5 ${tone} ${active ? 'ring-2 ring-gold/40' : ''}`}>
      <span className="text-[13px] font-black leading-none">{value}</span>
      <span>{label}</span>
    </button>
  );
}

// ── The panel: one renewal, whichever view it was opened from ───────────────
function RenewalPanel({ item, today, userId, onClose }) {
  const isAction = !!item.status;
  const [messages, setMessages] = useState(null);
  useEffect(() => {
    if (!isAction) return;
    setMessages(null);
    api.get(`clearance-renewals/actions/${item.id}/thread`)
      .then(d => setMessages(d.messages || []))
      .catch(() => setMessages([]));
  }, [isAction, item.id]);

  const onMe = isAction && item.status === 'open' && (item.assignees || []).some(p => p.person_id === userId);
  const started = isAction ? startedBy(item) : [];

  return (
    <aside data-tour="renewal-panel" className="bg-white border border-gray-200 rounded p-3 w-full lg:w-[340px] lg:shrink-0 self-start">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-[12px] font-bold text-navy flex items-center gap-1.5">
            {item.person_name}
            {item.left && <span className="text-[8px] font-semibold bg-gray-100 text-gray-500 px-1 rounded">Left</span>}
          </div>
          <div className="text-[9px] text-gray-500">{[item.designation, item.team_name].filter(Boolean).join(' · ') || ' '}</div>
        </div>
        <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 text-[14px] leading-none">×</button>
      </div>

      <div className="border border-gray-200 rounded p-2 mb-3 bg-gray-50 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <KindTag label={item.label} />
          <StateTag state={item.state} />
        </div>
        <div className="text-[10px] text-navy">Expiry date {formatDate(item.due_date || item.expiry)}</div>

        {!isAction && item.state === 'upcoming' && (
          <div className="text-[9px] text-gray-500">
            No renewal action yet. The PMO&apos;s action opens on {formatDate(item.opens_on)}
            {item.opens_on <= today ? ', on the next daily check' : ''}.
          </div>
        )}
        {!isAction && item.state === 'lapsed' && (
          <div className="text-[9px] text-orange-700">
            This date has passed and no renewal action was raised for it. Once it has been renewed, record the new date on their People record.
          </div>
        )}

        {isAction && item.status === 'open' && (
          <>
            <div className="text-[9px] text-gray-500">Raised {formatDate(stamp(item.created_at))} on the Project Management Office. The first of them to mark it done closes it for all.</div>
            <div className="text-[9px] text-gray-500">
              {started.length ? `Being worked by ${started.map(p => p.person_name).join(', ')}.` : 'Nobody has started it yet.'}
            </div>
          </>
        )}
        {isAction && item.status !== 'open' && (
          <>
            <div className="text-[9px] text-gray-500">
              {item.resolved_by_system ? 'Closed automatically' : `Marked done by ${item.resolved_by_name || 'someone'}`} on {formatDate(stamp(item.resolved_at))}.
            </div>
            {item.resolution_note && <div className="text-[9px] text-gray-600 italic">&ldquo;{item.resolution_note}&rdquo;</div>}
            {recordStillOld(item) ? (
              <div className="text-[9px] text-amber-700">Their People record still shows {formatDate(item.current_expiry)}, the date that was renewed. Record the new date there.</div>
            ) : (
              <div className="text-[9px] text-gray-500">
                {item.left ? 'They have since left.'
                  : item.current_expiry ? `The date on their record is now ${formatDate(item.current_expiry)}.`
                  : 'Their record has no expiry date now.'}
              </div>
            )}
          </>
        )}
        {onMe && (
          <a href={`#my-tasks/${item.id}`} className="inline-block mt-1 px-2 py-1 rounded text-[10px] font-semibold bg-navy text-white">Open in My Actions</a>
        )}
      </div>

      {isAction && (
        <>
          <div className="text-[9px] uppercase font-bold text-gray-500 mb-1">On the action</div>
          <ul className="mb-3 flex flex-wrap gap-1">
            {(item.assignees || []).map(p => (
              <li key={p.person_id} className={`text-[9px] px-1.5 py-px rounded-md border ${p.started_at && !p.completed_at ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-600 border-gray-200'}`}>
                {p.person_name}{p.started_at && !p.completed_at ? ' · started' : ''}{p.completed_at ? ' · done' : ''}
              </li>
            ))}
            {!(item.assignees || []).length && <li className="text-[9px] text-gray-400">Nobody</li>}
          </ul>

          <div className="text-[9px] uppercase font-bold text-gray-500 mb-1">Thread</div>
          {messages === null && <div className="text-[9px] text-gray-400">Loading…</div>}
          {messages && messages.length === 0 && <div className="text-[9px] text-gray-500">No messages.</div>}
          {messages && messages.length > 0 && (
            <ol className="divide-y divide-gray-100">
              {messages.map(m => (
                <li key={m.id} className="py-1.5">
                  <div className="text-[9px] text-gray-500">{m.author_name || 'Unknown'} · {formatDateTime(stamp(m.created_at))}</div>
                  <div className="text-[10px] text-navy whitespace-pre-wrap">{m.body}</div>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </aside>
  );
}

// ── Table views ─────────────────────────────────────────────────────────────
const TH = ({ children }) => (
  <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 whitespace-nowrap">{children}</th>
);

function Empty({ cols, children }) {
  return <tr><td colSpan={cols} className="px-2 py-3 text-[10px] text-gray-500">{children}</td></tr>;
}

function PersonCell({ r }) {
  return (
    <td className="px-2 py-1 text-[10px] font-semibold text-navy whitespace-nowrap">
      {r.person_name}
      {r.left && <span className="ml-1 text-[8px] font-semibold bg-gray-100 text-gray-500 px-1 rounded">Left</span>}
      {r.designation && <div className="text-[8px] text-gray-400 font-normal">{r.designation}</div>}
    </td>
  );
}

function InFlightTable({ rows, today, selected, onSelect }) {
  return (
    <table className="w-full">
      <thead><tr>{['Person', 'Renewal', 'Expires', 'Status', 'Being worked by', 'Latest update', 'Raised'].map(h => <TH key={h}>{h}</TH>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <Empty cols={7}>No renewals in flight.</Empty>}
        {rows.map(a => {
          const started = startedBy(a);
          return (
            <tr key={a.id} onClick={() => onSelect(a.id)}
              className={`cursor-pointer hover:bg-gold/5 ${selected === a.id ? 'bg-gold/10' : ''} ${a.state === 'overdue' ? 'bg-red-50/40' : ''}`}>
              <PersonCell r={a} />
              <td className="px-2 py-1"><KindTag label={a.label} /></td>
              <td className="px-2 py-1"><Due date={a.due_date} today={today} open /></td>
              <td className="px-2 py-1"><StateTag state={a.state} /></td>
              <td className="px-2 py-1 text-[9px] text-gray-600">
                {started.length ? started.map(p => p.person_name).join(', ') : <span className="text-gray-400">Not started</span>}
                <div className="text-[8px] text-gray-400">{(a.assignees || []).length} on the PMO</div>
              </td>
              <td className="px-2 py-1 text-[9px] text-gray-500 max-w-[260px]">
                <div className="truncate" title={a.last_message || ''}>{a.last_message || <span className="text-gray-400">None yet</span>}</div>
                {a.message_count > 0 && <div className="text-[8px] text-gray-400">{a.message_count} {a.message_count === 1 ? 'message' : 'messages'}</div>}
              </td>
              <td className="px-2 py-1 text-[9px] text-gray-500 whitespace-nowrap">{formatDate(stamp(a.created_at))}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ComingUpTable({ upcoming, lapsed, today, selected, onSelect }) {
  const row = (e) => {
    const ref = expiryRef(e);
    return (
      <tr key={ref} onClick={() => onSelect(ref)}
        className={`cursor-pointer hover:bg-gold/5 ${selected === ref ? 'bg-gold/10' : ''} ${e.state === 'lapsed' ? 'bg-orange-50/40' : ''}`}>
        <PersonCell r={e} />
        <td className="px-2 py-1"><KindTag label={e.label} /></td>
        <td className="px-2 py-1"><Due date={e.expiry} today={today} open /></td>
        <td className="px-2 py-1"><StateTag state={e.state} /></td>
        <td className="px-2 py-1 text-[9px] text-gray-500 whitespace-nowrap">
          {e.state === 'lapsed' ? 'Nothing was raised' : e.opens_on <= today ? 'On the next daily check' : formatDate(e.opens_on)}
        </td>
        <td className="px-2 py-1 text-[9px] text-gray-500">{e.team_name || '-'}</td>
      </tr>
    );
  };
  return (
    <table className="w-full">
      <thead><tr>{['Person', 'Renewal', 'Expires', 'Status', 'PMO action opens', 'Team'].map(h => <TH key={h}>{h}</TH>)}</tr></thead>
      <tbody>
        {lapsed.length > 0 && (
          <tr><td colSpan={6} className="px-2 pt-2 pb-1 text-[9px] font-bold uppercase text-orange-700">Lapsed with no renewal action ({lapsed.length})</td></tr>
        )}
        {lapsed.map(row)}
        {lapsed.length > 0 && upcoming.length > 0 && (
          <tr><td colSpan={6} className="px-2 pt-2 pb-1 text-[9px] font-bold uppercase text-gray-500">Coming up ({upcoming.length})</td></tr>
        )}
        {upcoming.length === 0 && lapsed.length === 0 && <Empty cols={6}>No expiry dates are waiting to be raised.</Empty>}
        {upcoming.map(row)}
      </tbody>
    </table>
  );
}

function CompletedTable({ rows, selected, onSelect }) {
  return (
    <table className="w-full">
      <thead><tr>{['Person', 'Renewal', 'Expiry renewed', 'Outcome', 'Closed', 'By', 'Date on record now'].map(h => <TH key={h}>{h}</TH>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <Empty cols={7}>No completed renewals.</Empty>}
        {rows.map(a => (
          <tr key={a.id} onClick={() => onSelect(a.id)}
            className={`cursor-pointer hover:bg-gold/5 ${selected === a.id ? 'bg-gold/10' : ''}`}>
            <PersonCell r={a} />
            <td className="px-2 py-1"><KindTag label={a.label} /></td>
            <td className="px-2 py-1 text-[10px] text-navy whitespace-nowrap">{formatDate(a.due_date)}</td>
            <td className="px-2 py-1"><StateTag state={a.state} /></td>
            <td className="px-2 py-1 text-[9px] text-gray-500 whitespace-nowrap">{formatDate(stamp(a.resolved_at))}</td>
            <td className="px-2 py-1 text-[9px] text-gray-500 whitespace-nowrap">{a.resolved_by_system ? 'Daily check' : (a.resolved_by_name || '-')}</td>
            <td className="px-2 py-1 text-[9px] whitespace-nowrap">
              {a.left ? <span className="text-gray-400">Left</span>
                : !a.current_expiry ? <span className="text-gray-400">None</span>
                : recordStillOld(a) ? <span className="text-amber-700 font-semibold">{formatDate(a.current_expiry)}, not updated</span>
                : <span className="text-gray-600">{formatDate(a.current_expiry)}</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Calendar ────────────────────────────────────────────────────────────────
function CalendarView({ entries, today, selected, onSelect }) {
  const [cursor, setCursor] = useState(() => ({ y: Number(today.slice(0, 4)), m: Number(today.slice(5, 7)) - 1 }));
  const [openDay, setOpenDay] = useState(null);
  const weeks = useMemo(() => monthWeeks(cursor.y, cursor.m), [cursor]);
  const byDay = useMemo(() => {
    const map = {};
    for (const e of entries) (map[e.date] ||= []).push(e);
    return map;
  }, [entries]);
  const move = (n) => { setOpenDay(null); setCursor(({ y, m }) => { const d = new Date(Date.UTC(y, m + n, 1)); return { y: d.getUTCFullYear(), m: d.getUTCMonth() }; }); };
  const inMonth = entries.filter(e => e.date.slice(0, 7) === `${cursor.y}-${String(cursor.m + 1).padStart(2, '0')}`).length;
  const SHOW = 3;

  return (
    <div data-tour="calendar" className="p-2">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <button onClick={() => move(-1)} aria-label="Previous month" className="px-2 py-0.5 rounded border border-gray-200 bg-white text-[11px] text-gray-600 hover:bg-gray-50">‹</button>
        <div className="text-[12px] font-bold text-navy min-w-[120px] text-center">{MONTHS[cursor.m]} {cursor.y}</div>
        <button onClick={() => move(1)} aria-label="Next month" className="px-2 py-0.5 rounded border border-gray-200 bg-white text-[11px] text-gray-600 hover:bg-gray-50">›</button>
        <button onClick={() => { setOpenDay(null); setCursor({ y: Number(today.slice(0, 4)), m: Number(today.slice(5, 7)) - 1 }); }}
          className="px-2 py-0.5 rounded border border-gray-200 bg-white text-[10px] text-gray-600 hover:bg-gray-50">Today</button>
        <span className="text-[9px] text-gray-500">{inMonth} {inMonth === 1 ? 'expiry' : 'expiries'} this month</span>
        <div className="ml-auto flex flex-wrap gap-1">
          {['overdue', 'not_started', 'in_progress', 'upcoming', 'lapsed', 'done', 'closed'].map(s => <StateTag key={s} state={s} />)}
        </div>
      </div>
      <div className="grid grid-cols-7 border-l border-t border-gray-200">
        {WEEKDAYS.map(d => <div key={d} className="px-1 py-0.5 text-[8px] uppercase font-bold text-gray-500 bg-gray-50 border-r border-b border-gray-200">{d}</div>)}
        {weeks.flat().map((date, i) => {
          if (!date) return <div key={`x${i}`} className="min-h-[84px] bg-gray-50 border-r border-b border-gray-200" />;
          const items = byDay[date] || [];
          const expanded = openDay === date;
          const shown = expanded ? items : items.slice(0, SHOW);
          return (
            <div key={date} className={`min-h-[84px] p-1 border-r border-b border-gray-200 ${date === today ? 'bg-gold/10' : 'bg-white'}`}>
              <div className={`text-[9px] mb-0.5 ${date === today ? 'font-black text-navy' : 'text-gray-500'}`}>{Number(date.slice(8))}</div>
              <div className="space-y-0.5">
                {shown.map(e => (
                  <button key={`${e.ref.type}-${e.ref.id}`} onClick={() => onSelect(e.ref.id)}
                    title={`${e.label}: ${e.person_name} (${STATES[e.state].label})`}
                    className={`block w-full text-left truncate px-1 py-px rounded border text-[8px] font-semibold ${STATES[e.state].tone} ${selected === e.ref.id ? 'ring-2 ring-gold/50' : ''}`}>
                    {e.label} · {e.person_name}
                  </button>
                ))}
                {items.length > SHOW && (
                  <button onClick={() => setOpenDay(expanded ? null : date)} className="text-[8px] text-gray-500 hover:text-navy">
                    {expanded ? 'Show fewer' : `+${items.length - SHOW} more`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── The page ────────────────────────────────────────────────────────────────
export default function ClearanceRenewalsPage({ tab: requestedTab }) {
  const { currentUser } = useApp() || {};
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(RENEWAL_TABS.some(t => t.id === requestedTab) ? requestedTab : 'in-flight');
  useEffect(() => { if (RENEWAL_TABS.some(t => t.id === requestedTab)) setTab(requestedTab); }, [requestedTab]);
  const [kind, setKind] = useState('all');
  const [search, setSearch] = useState('');
  const [onlyState, setOnlyState] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    api.get('clearance-renewals').then(d => { setData(d); setError(null); }).catch(e => setError(e.message || 'Could not load renewals.'));
  }, []);
  useEffect(load, [load]);
  useRefreshOnFocus(load);

  const tour = useFeatureTour(CLEARANCE_RENEWALS_TOUR_ID, { enabled: !!data });

  const today = data?.today || new Date().toISOString().slice(0, 10);
  const f = useMemo(() => ({ kind, search }), [kind, search]);
  const tiles = useMemo(() => (data ? summary(data, today) : null), [data, today]);
  const flight = useMemo(() => inFlight(data, today, f), [data, today, f]);
  const done = useMemo(() => completed(data, today, f), [data, today, f]);
  const loose = useMemo(() => unraised(data, today, f), [data, today, f]);
  const entries = useMemo(() => calendarEntries(data, today, f), [data, today, f]);

  const flightRows = onlyState ? flight.filter(a => a.state === onlyState) : flight;
  const soon = addDays(today, 90);
  const comingUp = onlyState === 'lapsed' ? [] : onlyState === 'soon' ? loose.upcoming.filter(e => e.expiry <= soon) : loose.upcoming;
  const lapsed = onlyState === 'soon' ? [] : loose.lapsed;

  // The panel finds its item wherever it lives, so a chip on the calendar and
  // a row in a table open the same thing.
  const selected = useMemo(() => {
    if (!selectedId || !data) return null;
    const a = [...flight, ...done].find(x => x.id === selectedId);
    if (a) return a;
    return [...loose.upcoming, ...loose.lapsed].find(e => expiryRef(e) === selectedId) || null;
  }, [selectedId, data, flight, done, loose]);

  const go = (t, state = null) => { setTab(t); setOnlyState(state); };

  return (
    <div className="p-4 fade-in">
      <div data-tour="page-header" className="mb-3">
        <h1 className="text-[15px] font-black text-navy flex items-center gap-2">
          Clearance &amp; RDN Renewals
          <button onClick={tour.start} title="Replay the guided tour" aria-label="Replay the guided tour"
            className="w-4 h-4 rounded-full border border-gray-300 text-[9px] text-gray-500 hover:bg-gray-100 leading-none">?</button>
        </h1>
        <div className="text-[9px] text-gray-500 mt-0.5">
          Where every security clearance, site pass and RDN renewal has got to. The Project Management Office works them in My Actions; this page shows their progress and changes nothing.
        </div>
      </div>

      {error && <div className="text-[10px] text-red-600 mb-2">{error}</div>}

      <div data-tour="tiles" className="flex items-center gap-2 mb-3 flex-wrap">
        <Tile value={tiles ? tiles.inFlight : '–'} label="in flight" tone="border-gray-200 bg-white text-navy"
          active={tab === 'in-flight' && !onlyState} onClick={() => go('in-flight')} />
        <Tile value={tiles ? tiles.overdue : '–'} label="overdue" tone={tiles?.overdue ? 'border-red-200 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-600'}
          active={onlyState === 'overdue'} onClick={() => go('in-flight', onlyState === 'overdue' ? null : 'overdue')} />
        <Tile value={tiles ? tiles.notStarted : '–'} label="not started" tone={tiles?.notStarted ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-gray-200 bg-white text-gray-600'}
          active={onlyState === 'not_started'} onClick={() => go('in-flight', onlyState === 'not_started' ? null : 'not_started')} />
        <Tile value={tiles ? tiles.comingUp : '–'} label="expiring in the next 90 days, not yet raised" tone="border-gray-200 bg-white text-gray-600"
          active={onlyState === 'soon'} onClick={() => go('coming-up', onlyState === 'soon' ? null : 'soon')} />
        <Tile value={tiles ? tiles.lapsed : '–'} label="lapsed with no action" tone={tiles?.lapsed ? 'border-orange-200 bg-orange-50 text-orange-700' : 'border-gray-200 bg-white text-gray-600'}
          active={onlyState === 'lapsed'} onClick={() => go('coming-up', onlyState === 'lapsed' ? null : 'lapsed')} />
        <Tile value={tiles ? tiles.completed90 : '–'} label="completed in the last 90 days" tone="border-gray-200 bg-white text-gray-600"
          active={tab === 'completed'} onClick={() => go('completed')} />
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div data-tour="tabs" className="flex gap-1">
          {RENEWAL_TABS.map(t => (
            <button key={t.id} onClick={() => go(t.id)}
              className={`px-3 py-1 text-[10px] font-semibold rounded ${tab === t.id ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {t.label}
              {t.id === 'in-flight' && tiles?.inFlight > 0 && <span className="ml-1 bg-amber-400 text-amber-950 text-[8px] font-bold px-1 rounded-full">{tiles.inFlight}</span>}
            </button>
          ))}
        </div>
        <div data-tour="filters" className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden w-fit">
            {KIND_FILTERS.map(k => (
              <button key={k.id} onClick={() => setKind(k.id)}
                className={`px-2 py-1 text-[10px] ${kind === k.id ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                {k.label}
              </button>
            ))}
          </div>
          <input className="form-input max-w-[220px]" placeholder="Search name, role or team" value={search} onChange={e => setSearch(e.target.value)} />
          {onlyState && (
            <button onClick={() => setOnlyState(null)} className="text-[9px] text-gray-500 hover:text-navy underline">
              Showing {onlyState === 'soon' ? 'the next 90 days' : STATES[onlyState].label.toLowerCase()} only. Show all
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 items-start">
        <div data-tour="list" className="bg-white border border-gray-200 rounded overflow-x-auto flex-1 min-w-0 w-full">
          {!data && !error && <div className="px-2 py-3 text-[10px] text-gray-400">Loading…</div>}
          {data && tab === 'in-flight' && <InFlightTable rows={flightRows} today={today} selected={selectedId} onSelect={setSelectedId} />}
          {data && tab === 'coming-up' && <ComingUpTable upcoming={comingUp} lapsed={lapsed} today={today} selected={selectedId} onSelect={setSelectedId} />}
          {data && tab === 'completed' && <CompletedTable rows={done} selected={selectedId} onSelect={setSelectedId} />}
          {data && tab === 'calendar' && <CalendarView entries={entries} today={today} selected={selectedId} onSelect={setSelectedId} />}
        </div>

        {selected && (
          <RenewalPanel key={selectedId} item={selected} today={today} userId={currentUser?.id} onClose={() => setSelectedId(null)} />
        )}
      </div>

      {tour.open && <FeatureTour steps={clearanceRenewalsTourSteps} onFinish={tour.finish} onSkip={tour.skip} />}
    </div>
  );
}
